import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from 'types/documentServices';
import gfppDataUtils from './gfppDataUtils';
import { updateColumnToNewSkin } from './columnsGfppUtils';
import constants from '#packages/constants';
import * as coreBi from '#packages/coreBi';
import {
  stripUtils,
  behaviorsUtils,
  designData as designDataUtils,
} from '#packages/util';

export const handleOnDesignPanelClick = async (
  editorAPI: EditorAPI,
  compRef: CompRef | CompRef[],
  origin: string,
) => {
  const originalCompType = editorAPI.components.getType(compRef);
  let designPanelCompRef = Array.isArray(compRef) ? compRef[0] : compRef;

  if (originalCompType === constants.COMP_TYPES.STRIP_COLUMNS_CONTAINER) {
    const isSingleColumn = editorAPI.columns.isStripWithSingleColumn(compRef);
    const hasDividersDesign = designDataUtils.hasDividersDesign(
      editorAPI,
      compRef,
    );

    if (isSingleColumn && !hasDividersDesign) {
      designPanelCompRef = editorAPI.components.getChildren(compRef)[0];
      await updateColumnToNewSkin(editorAPI, designPanelCompRef);
    } else {
      await updateStripColumnsContainerToNewSkin(editorAPI, designPanelCompRef);
    }
  }

  gfppDataUtils.toggleComponentPanel(
    editorAPI,
    constants.PANELS.DESIGN.DESIGN_PANEL_NAME,
    {
      title: editorAPI.addPanel.getDesignPanelTitleByComp(compRef),
      selectedComponent: designPanelCompRef,
      startWithAdvanced: true,
      onlyAdvancedStyling: true,
      origin,
      /* WEED-33155 | Temporary solution will remove on merge dm_bgScrubToMotionFixer */
      onClose: () =>
        onCloseStripColumnsContainerDesignPanel(editorAPI, designPanelCompRef),
    },
    editorAPI.components.getType(compRef),
  );
};

/* WEED-33155 | Temporary solution will remove on merge dm_bgScrubToMotionFixer */
const onCloseStripColumnsContainerDesignPanel = (
  editorAPI: EditorAPI,
  compRef: CompRef,
) => {
  const hasProblematicScrollEffect =
    stripUtils.isStripOrColumnHasProblematicScrollEffect(editorAPI, compRef);
  const isStripOrColumnHasVisibleStyle =
    stripUtils.isStripOrColumnHasVisibleStyle(editorAPI, compRef);

  if (!hasProblematicScrollEffect || !isStripOrColumnHasVisibleStyle) {
    return;
  }

  behaviorsUtils.removeBehaviorsByAction(
    editorAPI,
    compRef,
    'bgScrub',
    'DESKTOP',
  );
};

export const updateStripColumnsContainerToNewSkin = async (
  editorAPI: EditorAPI,
  compRef: CompRef,
) => {
  const compStyle = editorAPI.components.style.get(compRef);

  const isSkinNeedToBeUpdate =
    compStyle.skin !== constants.SKINS.DESIGNED_STRIP_CONTAINER;
  if (!isSkinNeedToBeUpdate) {
    return;
  }

  compStyle.skin = constants.SKINS.DESIGNED_STRIP_CONTAINER;
  compStyle.style = constants.STRIP.getBaseStyledStripStyle();

  try {
    editorAPI.components.style.update(compRef, compStyle, true);
    await editorAPI.waitForChangesAppliedAsync();

    const updatedCompStyle = editorAPI.components.style.get(compRef);
    if (updatedCompStyle.skin !== constants.SKINS.DESIGNED_STRIP_CONTAINER) {
      throw new Error(
        'Failed to update strip columns container to designedStrip skin',
      );
    }
  } catch (e: any) {
    editorAPI.bi.event(
      coreBi.events.switchStripColumnSkin.skin_switching_error,
      {
        component_id: compRef.id,
        component_type: editorAPI.components.getType(compRef),
        error_code: 'failed_to_update_skin',
        error_message: e?.message || 'Unknown error',
      },
    );
    return;
  }

  editorAPI.bi.event(
    coreBi.events.switchStripColumnSkin.skin_switched_successfully,
    {
      component_id: compRef.id,
      component_type: editorAPI.components.getType(compRef),
    },
  );
};
